import {  t  } from "@/client/libs/dummy-i18n";
import type { ResumeDto } from "@stackmorph/dto";
import {
  AlignmentType,
  Document,
  HeadingLevel,
  Packer,
  Paragraph,
  TabStopPosition,
  TabStopType,
  TextRun,
} from "docx";
import { saveAs } from "file-saver";

export const exportDocx = async (resume: ResumeDto) => {
  const { basics, sections } = resume.data;

  const children: Paragraph[] = [];

  // Header (Basics)
  children.push(
    new Paragraph({
      text: basics.name,
      heading: HeadingLevel.TITLE,
      alignment: AlignmentType.CENTER,
    }),
  );

  if (basics.headline) {
    children.push(
      new Paragraph({
        text: basics.headline,
        heading: HeadingLevel.HEADING_2,
        alignment: AlignmentType.CENTER,
      }),
    );
  }

  const contactInfo: string[] = [];
  if (basics.email) contactInfo.push(basics.email);
  if (basics.phone) contactInfo.push(basics.phone);
  if (basics.url.href) contactInfo.push(basics.url.href);
  if (basics.location) contactInfo.push(basics.location);

  if (contactInfo.length > 0) {
    children.push(
      new Paragraph({
        text: contactInfo.join(" | "),
        alignment: AlignmentType.CENTER,
      }),
    );
  }

  children.push(new Paragraph({ text: "" })); // Spacing

  const sectionOrder = [
    "summary",
    "experience",
    "education",
    "skills",
    "projects",
    "certifications",
    "awards",
    "volunteer",
    "references",
    "publications",
    "languages",
    "interests",
  ] as const;

  for (const sectionId of sectionOrder) {
    const section = sections[sectionId];
    if (!section?.visible) continue;

    // Section Title
    children.push(
      new Paragraph({
        text: section.name,
        heading: HeadingLevel.HEADING_1,
        thematicBreak: true,
      }),
    );

    switch (sectionId) {
      case "summary": {
        const summarySection = section as typeof sections.summary;
        children.push(new Paragraph({ text: summarySection.content }));
        break;
      }

      case "experience":
      case "volunteer": {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const listSection = section as any;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const item of listSection.items as any[]) {
          if (!item.visible) continue;
          
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: item.position, bold: true, size: 24 }),
                new TextRun({
                  text: `\t${item.date}`,
                  bold: true,
                }),
              ],
              tabStops: [
                {
                  type: TabStopType.RIGHT,
                  position: TabStopPosition.MAX,
                },
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: item.company ?? item.organization, italics: true }),
                new TextRun({
                  text: `\t${item.location ?? ""}`,
                  italics: true,
                }),
              ],
              tabStops: [
                {
                  type: TabStopType.RIGHT,
                  position: TabStopPosition.MAX,
                },
              ],
            }),
            new Paragraph({ text: item.summary }),
            new Paragraph({ text: "" })
          );
        }
        break;
      }

      case "education": {
        const eduSection = section as typeof sections.education;
        for (const item of eduSection.items) {
          if (!item.visible) continue;

          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: item.institution, bold: true, size: 24 }),
                new TextRun({
                  text: `\t${item.date}`,
                  bold: true,
                }),
              ],
              tabStops: [
                {
                  type: TabStopType.RIGHT,
                  position: TabStopPosition.MAX,
                },
              ],
            }),
            new Paragraph({
              children: [
                new TextRun({ text: `${item.studyType} - ${item.area}`, italics: true }),
                new TextRun({
                  text: `\t${item.score ? `${t`GPA`}: ${item.score}` : ""}`,
                  italics: true,
                }),
              ],
              tabStops: [
                {
                  type: TabStopType.RIGHT,
                  position: TabStopPosition.MAX,
                },
              ],
            }),
            new Paragraph({ text: item.summary }),
            new Paragraph({ text: "" })
          );
        }
        break;
      }

      case "skills": {
        const skillsSection = section as typeof sections.skills;
        for (const item of skillsSection.items) {
          if (!item.visible) continue;
          children.push(
            new Paragraph({
              children: [
                new TextRun({ text: item.name, bold: true }),
                new TextRun({ text: `: ${item.description ?? item.level}` }),
              ],
              bullet: { level: 0 },
            }),
          );
        }
        break;
      }

      default: {
        // Generic fallback
        if ("items" in section) {
           // eslint-disable-next-line @typescript-eslint/no-explicit-any
          for (const item of (section as any).items) {
            if (!item.visible) continue;
            
            const title = item.name || item.title || item.institution || item.company;
            if (title) {
              children.push(
                new Paragraph({
                  text: title,
                  bullet: { level: 0 },
                }),
              );
            }
            
            if (item.summary) {
              children.push(new Paragraph({ text: item.summary, indent: { left: 720 } }));
            }
          }
        }
        break;
      }
    }

    children.push(new Paragraph({ text: "" }));
  }

  // Footer
  children.push(
    new Paragraph({
      children: [
        new TextRun({
          text: t`Generated by StackMorph Resume Builder`,
          italics: true,
          size: 16,
          color: "808080",
        }),
      ],
      alignment: AlignmentType.CENTER,
    }),
  );

  const doc = new Document({
    sections: [
      {
        properties: {},
        children: children,
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const filename = `${resume.slug || "resume"}.docx`;

  saveAs(blob, filename);
};
